import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom'; // Added useNavigate
import {
  MagnifyingGlassIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon,
  PrinterIcon // Added PrinterIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
// Removed jsPDF and jspdf-autotable imports
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import { ru } from 'date-fns/locale/ru';
import { format as formatDateFns } from 'date-fns'; // Use date-fns for formatting
import Select from 'react-select'; // Import react-select

// Register Russian locale for DatePicker
registerLocale('ru', ru);

// Removed jsPDF declaration block

// Updated interface to match data from /structures/active
interface Structure {
  id: number;
  code: string;
  type: string;
  name: string; // Constructed in backend
  address: string; // Mapped from location
  size: string; // Mapped from dimensions/screenSize
  isActive: boolean; // Always true
  currentDailyPrice: number | null; // Added missing field
  currentMonthlyPrice: number | null; // Added missing field
  imageUrl?: string; // Added for structure image
}

// Add SortConfig type
interface SortConfig {
  key: keyof Structure | null;
  direction: 'ascending' | 'descending';
}

const AvailableStructures: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate(); // Added useNavigate
  const { can } = useAuth();

  // Initial state from URL params or defaults
  const getInitialDates = (): [Date | null, Date | null] => {
    const start = searchParams.get('startDate');
    const end = searchParams.get('endDate');
    const today = new Date();
    const nextWeek = new Date();
    nextWeek.setDate(today.getDate() + 7);
    return [
      start ? new Date(start) : today,
      end ? new Date(end) : nextWeek
    ];
  };

  const [availableStructures, setAvailableStructures] = useState<Structure[]>([]);
  const [structureTypes, setStructureTypes] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState<string>(searchParams.get('type') || 'all');
  const [selectedDateRange, setSelectedDateRange] = useState<[Date | null, Date | null]>(getInitialDates());
  // Removed currentMonth and occupiedDates state
  const [loadingTypes, setLoadingTypes] = useState(true); // Loading state for types
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'code', direction: 'ascending' }); // State for sorting
  const [selectedForPrintIds, setSelectedForPrintIds] = useState<Set<number>>(new Set());
  const [excludeServiceType, setExcludeServiceType] = useState<boolean>(true); // New state for excluding "Услуга"

  const isCityboard = (structureType: string) => structureType === 'Ситиборд';

  const shouldShowDailyPriceForGroup = (structures: Structure[]): boolean => {
    return structures.some(structure => !isCityboard(structure.type));
  };

  // Fetch AVAILABLE structures based on date range and type
  useEffect(() => {
    const [startDate, endDate] = selectedDateRange;

    if (startDate && endDate) {
      setLoading(true);
      setError(null);

      const currentParams = new URLSearchParams(searchParams);
      currentParams.set('startDate', formatDateFns(startDate, 'yyyy-MM-dd'));
      currentParams.set('endDate', formatDateFns(endDate, 'yyyy-MM-dd'));

      const apiParams: Record<string, string> = {
        startDate: formatDateFns(startDate, 'yyyy-MM-dd'),
        endDate: formatDateFns(endDate, 'yyyy-MM-dd'),
      };

      if (selectedType !== 'all') {
        apiParams.type = selectedType;
        currentParams.set('type', selectedType);
      } else {
        currentParams.delete('type');
      }

      if (searchTerm) {
        apiParams.search = searchTerm;
        currentParams.set('search', searchTerm);
      } else {
        currentParams.delete('search');
      }
      
      // Client-side filtering will handle excluding "Услуга", so no apiParams.exclude_types needed here.

      setSearchParams(currentParams, { replace: true });

      axios.get('/structures/available', { params: apiParams })
        .then(response => {
          const structures: Structure[] = response.data.filter((structure: Structure) => structure.type !== 'Услуга' || !excludeServiceType);
          
          setAvailableStructures(structures);
          // Automatically select all loaded structures for printing
          const newSelectedIds = new Set(structures.map(s => s.id));
          setSelectedForPrintIds(newSelectedIds);
        })
        .catch(error => {
          console.error('Error fetching available structures:', error);
          setError('Ошибка при загрузке данных о доступных конструкциях');
          setAvailableStructures([]);
          setSelectedForPrintIds(new Set()); // Clear selection on error
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setAvailableStructures([]);
      setSelectedForPrintIds(new Set()); // Clear selection if dates are invalid
      setLoading(false);
    }
  }, [selectedDateRange, selectedType, searchTerm, excludeServiceType, setSearchParams]);

  // Fetch structure types
  useEffect(() => {
      setLoadingTypes(true);
      // Correct the endpoint URL
      axios.get('/settings/structure-types') 
          .then(response => {
              // Assuming response.data is an array of strings
              setStructureTypes(response.data || []);
          })
          .catch(err => {
              console.error('Error fetching structure types:', err);
              // Fallback or show error
              setStructureTypes([]); 
          })
          .finally(() => {
              setLoadingTypes(false);
          });
  }, []);

  // Prepare options for react-select type filter
  const typeOptions = [
    { value: 'all', label: 'Все типы' },
    ...structureTypes.map(type => ({ value: type, label: type }))
  ];

  // Helper function to format currency (add if not already globally available)
  const formatCurrency = (amount: number | null): string => {
    if (amount === null || isNaN(amount)) return '-';
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Removed getStructureOccupiedDates, isDateOccupied, month navigation functions

  const formatDisplayDate = (date: Date | null): string => {
    if (!date) return '...';
    return formatDateFns(date, 'dd.MM.yyyy');
  };
  
  // Removed formatDisplayRange as it's not used directly here anymore for PDF generation
  // const formatDisplayRange = (): string => { ... }


  // Function to handle navigation to print page
  const handlePrintAvailability = () => {
    const [startDate, endDate] = selectedDateRange;
    if (!startDate || !endDate) {
      setError("Пожалуйста, выберите диапазон дат для печати.");
      return;
    }
    if (selectedForPrintIds.size === 0) {
      setError("Пожалуйста, выберите хотя бы одну конструкцию для печати.");
      return;
    }
  
    const params = new URLSearchParams();
    params.append('startDate', formatDateFns(startDate, 'yyyy-MM-dd'));
    params.append('endDate', formatDateFns(endDate, 'yyyy-MM-dd'));
    if (selectedType !== 'all') {
      params.append('type', selectedType);
    }
    if (searchTerm) {
      params.append('search', searchTerm);
    }
    params.append('structure_ids', Array.from(selectedForPrintIds).join(','));
    
    navigate(`/structures/availability/print?${params.toString()}`);
  };

  // Apply client-side filtering and sorting
  const sortedStructures = useMemo(() => {
    let itemsToDisplay = [...availableStructures];

    // Client-side filtering for "Услуга" type
    if (excludeServiceType && selectedType !== 'Услуга') {
      itemsToDisplay = itemsToDisplay.filter(structure => structure.type !== 'Услуга');
    }

    // Sorting
    if (sortConfig.key !== null) {
      itemsToDisplay.sort((a, b) => {
        const aValue = a[sortConfig.key!];
        const bValue = b[sortConfig.key!];

        // Handle null/undefined values (treat them as lowest or highest depending on direction)
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (bValue == null) return sortConfig.direction === 'ascending' ? 1 : -1;

        // Actual comparison
        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
        } else {
            const stringA = String(aValue).toLowerCase();
            const stringB = String(bValue).toLowerCase();
            if (stringA < stringB) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (stringA > stringB) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        }
      });
    }
    return itemsToDisplay;
  }, [availableStructures, sortConfig, excludeServiceType, selectedType]);

  const groupedAndSortedStructures = useMemo(() => {
    if (!sortedStructures) return {};

    return sortedStructures.reduce((acc, structure) => {
      const type = structure.type || 'Uncategorized'; // Handle cases where type might be missing
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(structure);
      return acc;
    }, {} as Record<string, Structure[]>);
  }, [sortedStructures]);
  
  // --- Selection Logic ---
  const handleSelectStructure = (structureId: number) => {
    setSelectedForPrintIds(prevSelectedIds => {
      const newSelectedIds = new Set(prevSelectedIds);
      if (newSelectedIds.has(structureId)) {
        newSelectedIds.delete(structureId);
      } else {
        newSelectedIds.add(structureId);
      }
      return newSelectedIds;
    });
  };
  
  const handleSelectAllStructures = () => {
    if (sortedStructures.length === 0) return;
  
    const allCurrentIds = new Set(sortedStructures.map(s => s.id));
    // If all currently displayed structures are already selected, deselect them.
    // Otherwise, select all currently displayed structures.
    let allSelected = true;
    for (const structure of sortedStructures) {
      if (!selectedForPrintIds.has(structure.id)) {
        allSelected = false;
        break;
      }
    }
  
    if (allSelected) {
      // Deselect all currently visible
      setSelectedForPrintIds(prevSelectedIds => {
        const newSelectedIds = new Set(prevSelectedIds);
        sortedStructures.forEach(s => newSelectedIds.delete(s.id));
        return newSelectedIds;
      });
    } else {
      // Select all currently visible
      setSelectedForPrintIds(prevSelectedIds => {
        const newSelectedIds = new Set(prevSelectedIds);
        sortedStructures.forEach(s => newSelectedIds.add(s.id));
        return newSelectedIds;
      });
    }
  };
  
  const areAllStructuresSelected = useMemo(() => {
    if (sortedStructures.length === 0) return false;
    return sortedStructures.every(s => selectedForPrintIds.has(s.id));
  }, [sortedStructures, selectedForPrintIds]);
  // --- End Selection Logic ---
  
  // Function to handle sorting request
  const requestSort = (key: keyof Structure) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Function to render sort icons
  const renderSortIcon = (columnKey: keyof Structure) => {
    if (sortConfig.key !== columnKey) {
      return null; // Or a default subtle icon
    }
    return sortConfig.direction === 'ascending' ? (
      <ArrowUpIcon className="h-4 w-4 ml-1 inline-block text-gray-500" />
    ) : (
      <ArrowDownIcon className="h-4 w-4 ml-1 inline-block text-gray-500" />
    );
  };

  // Render logic
  return (
    <div>
      <PageHeader
        title="Свободные конструкции"
        subtitle={`Просмотр доступных конструкций на выбранный период`}
        backLink="/structures"
        actions={
          <button
            onClick={handlePrintAvailability}
            className="btn-primary flex items-center"
            disabled={
              loading ||
              availableStructures.length === 0 ||
              !selectedDateRange[0] ||
              !selectedDateRange[1] ||
              selectedForPrintIds.size === 0
            }
          >
            <PrinterIcon className="h-5 w-5 mr-1" />
            Распечатать свободку
          </button>
        }
      />

      <p className="text-sm text-gray-600 mb-4">Цены указаны в тенге с учетом НДС.</p>

      {/* Filters Row */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow flex flex-wrap items-center gap-4">
        {/* Date Range Picker */}
        <div className="flex-grow sm:flex-grow-0">
          <label htmlFor="dateRange" className="block text-sm font-medium text-gray-700 mb-1">
            Период доступности
          </label>
          <DatePicker
            selectsRange={true}
            startDate={selectedDateRange[0]}
            endDate={selectedDateRange[1]}
            onChange={(update) => setSelectedDateRange(update)}
            isClearable={false}
            dateFormat="dd.MM.yyyy"
            locale="ru"
            className="form-input w-full sm:w-64" // Adjust width as needed
            wrapperClassName="w-full sm:w-auto"
            popperClassName="z-[100]" // Ensure DatePicker popper is above sticky table headers
            placeholderText="Выберите диапазон дат"
            minDate={new Date()} // Prevent selecting past dates
          />
        </div>

        {/* Search Input */}
        <div className="flex-grow sm:flex-grow-0">
            <label htmlFor="search-availability" className="form-label mb-1">Поиск</label>
            <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                <input
                    id="search-availability"
                    type="text"
                    placeholder="Код, название, адрес..."
                    className="form-input pl-10 w-full sm:w-64"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)} // Update state, useEffect will refetch
                />
            </div>
        </div>

        {/* Type Filter */}
        <div className="flex-grow sm:flex-grow-0 min-w-[180px]">
          <label htmlFor="typeFilter" className="block text-sm font-medium text-gray-700 mb-1">
            Тип конструкции
          </label>
          <Select
            id="typeFilter"
            instanceId="availability-type-filter"
            options={typeOptions}
            value={typeOptions.find(opt => opt.value === selectedType)}
            onChange={(option: any) => setSelectedType(option?.value || 'all')}
            isLoading={loadingTypes}
            placeholder="Выберите тип..."
            classNamePrefix="react-select"
            styles={{ menu: (provided) => ({ ...provided, zIndex: 100 }) }} // Ensure Select menu is above sticky table headers
          />
        </div>

        {/* Exclude Service Type Checkbox */}
        <div className="flex items-center pt-5 sm:pt-6"> {/* Increased sm top padding for better alignment */}
          <input
            id="excludeServiceType"
            type="checkbox"
            checked={excludeServiceType}
            onChange={(e) => setExcludeServiceType(e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary-dark border-gray-300 rounded"
          />
          <label htmlFor="excludeServiceType" className="ml-2 block text-sm text-gray-700">
            Исключить тип "Услуга"
          </label>
        </div>
      </div>

      {/* Results Table */}
      {loading ? (
        <LoadingSpinner />
      ) : availableStructures.length === 0 ? (
        <div className="text-center p-6 bg-white rounded-lg shadow">
            <p className="text-gray-500">
                {searchTerm || selectedType !== 'all'
                    ? 'Свободные конструкции, соответствующие критериям, не найдены.'
                    : 'Нет свободных конструкций на выбранный период.'}
            </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-2 py-3 text-center">
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-primary focus:ring-primary-dark border-gray-300 rounded"
                      checked={areAllStructuresSelected}
                      onChange={handleSelectAllStructures}
                      disabled={sortedStructures.length === 0}
                      title={areAllStructuresSelected ? "Снять выделение со всех" : "Выделить все для печати"}
                    />
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('code')}
                  >
                    Код {renderSortIcon('code')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('name')}
                  >
                    Название {renderSortIcon('name')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('type')}
                  >
                    Тип {renderSortIcon('type')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Адрес
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Размер
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('currentDailyPrice')}
                  >
                    Цена/день {renderSortIcon('currentDailyPrice')}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('currentDailyPrice')}
                  >
                    {shouldShowDailyPriceForGroup(sortedStructures) && (
                      <>
                        Цена/день {renderSortIcon('currentDailyPrice')}
                      </>
                    )}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/*
                  The main loading and "no data" states are handled by the ternary operator wrapping the entire table (lines 416-426).
                  If we reach this tbody, loading is false and availableStructures.length > 0 (which implies sortedStructures.length > 0 unless filters are extremely restrictive).
                */}
                {Object.entries(groupedAndSortedStructures).map(([type, structuresInGroup]) => (
                  <React.Fragment key={type}>
                    <tr>
                      <td colSpan={9} className="px-6 py-3 bg-gray-100 text-left text-sm font-semibold text-gray-700 sticky top-0 z-10">
                        {type} ({structuresInGroup.length})
                      </td>
                    </tr>
                    {structuresInGroup.map((structure, index) => (
                      <tr key={structure.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${selectedForPrintIds.has(structure.id) ? 'bg-blue-100 hover:bg-blue-200' : 'hover:bg-gray-100'}`}>
                        <td className="px-2 py-4 text-center">
                          <input
                            type="checkbox"
                            className="form-checkbox h-4 w-4 text-primary focus:ring-primary-dark border-gray-300 rounded"
                            checked={selectedForPrintIds.has(structure.id)}
                            onChange={() => handleSelectStructure(structure.id)}
                          />
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {structure.code}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {structure.name || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {structure.type} {/* Kept for consistency, though group header shows type */}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {structure.address}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {structure.size || '-'}
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-gray-700">
                          {!isCityboard(structure.type) ? formatCurrency(structure.currentDailyPrice) : '-'}
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-gray-700">
                          {formatCurrency(structure.currentMonthlyPrice)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium">
                          <Link
                            to={`/structures/${structure.id}`}
                            className="text-primary hover:text-primary-dark p-1"
                            title="Детали"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {/* Fallback if sortedStructures is populated but grouping results in empty (should not happen with current logic) or if sortedStructures itself is empty after filtering */}
                {sortedStructures.length > 0 && Object.keys(groupedAndSortedStructures).length === 0 && (
                    <tr><td colSpan={9} className="p-6 text-center text-gray-500">Нет данных для отображения после группировки.</td></tr>
                )}
                {/* This specific message for "no structures found" is primarily handled by the outer condition (line 418),
                    but this ensures a message if sortedStructures becomes empty after initial load and filtering. */}
                {sortedStructures.length === 0 && !loading && (
                     <tr><td colSpan={9} className="p-6 text-center text-gray-500">Свободные конструкции, соответствующие фильтрам, не найдены.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailableStructures;
